import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Card, CardContent, Chip, Divider, Tooltip, Typography } from "@mui/material";

import { zkApi } from "./attendance/zkApi";
import { chipSx } from "./attendance/zkStatusStyles";
import { backButtonSx } from "./hpcAdminStatusStyles";
import DoorAccessDialog, { backToNormal } from "./attendance/DoorAccessDialog";
import { actionFor, doorStatus, pendingNote, plainReason, statusLine } from "./attendance/doorAccessText";

// "Gym door" box on the HPU member profile (hpc_admin only - the route is already restricted).
// Hidden when the door module is not available, or door control is OFF and the member is not blocked.
export default function HpcMemberDoorAccess({ registration }) {
  const membershipId = registration?.membership_id;
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null);

  const load = useCallback(async () => {
    if (!membershipId) return;
    try {
      const res = await zkApi("get", `/hpc/admin/zk/access/member/${encodeURIComponent(membershipId)}`);
      setData(res.data);
    } catch (e) {
      setData(null);            // door module not installed / not migrated: just don't show the box
    }
  }, [membershipId]);

  useEffect(() => { load(); }, [load, registration]);

  if (!data) return null;
  const items = data.items || [];
  const mode = data.settings?.mode;
  if (mode === "off" && !items.some((r) => r.applied === "blocked")) return null;

  const onAction = async (row, a) => {
    if (a.key === "normal") {
      setBusy(true);
      if (await backToNormal(row)) load();
      setBusy(false);
      return;
    }
    setDialog({ ...row, action: a.action });
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
          <Typography variant="h5" fontWeight="bold">Gym door</Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>{statusLine(data.settings, items[0]?.paused).text}</Typography>
            <Button variant="outlined" size="small" sx={backButtonSx} onClick={load}>Refresh</Button>
          </Box>
        </Box>
        <Divider sx={{ my: 2 }} />
        {!data.on_device && (
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            Not on the door device yet (no PIN enrolled for this member).
          </Typography>
        )}
        {items.map((row) => {
          const s = doorStatus(row);
          const a = actionFor(row);
          const note = pendingNote(row, { mode, paused: row.paused, guardTripped: false });
          return (
            <Box key={`${row.serial_number}-${row.pin}`} display="flex" alignItems="center" gap={2} flexWrap="wrap" sx={{ mb: 1 }}>
              <Tooltip title={s.help}><Chip label={s.label} sx={chipSx(s.color)} /></Tooltip>
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Typography variant="body1">{plainReason(row)}</Typography>
                {note && <Typography variant="caption" sx={{ color: "#ff9800" }}>{note}</Typography>}
                <Typography variant="caption" sx={{ display: "block", opacity: 0.6 }}>Door PIN {row.pin}</Typography>
              </Box>
              {a && (
                <Tooltip title={a.help}>
                  <span>
                    <Button variant={a.key === "normal" ? "outlined" : "contained"} disabled={busy}
                      color={a.key === "block" ? "error" : a.key === "let_in" ? "success" : "inherit"}
                      onClick={() => onAction(row, a)}>
                      {a.label}
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Box>
          );
        })}
      </CardContent>
      <DoorAccessDialog target={dialog} sn={dialog?.serial_number} today={data.today} onClose={() => setDialog(null)}
        onDone={() => { setDialog(null); load(); }} />
    </Card>
  );
}
